import { useState, useRef, useCallback, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useEvent } from "react-use"

import { isBrowser } from "utils/infrastructure/ssg-utils"

export function useStateWithCallback<StateType, CallbackType extends (state: StateType) => void>(
    initialState: StateType
): [StateType, (state: StateType, cb?: CallbackType) => void] {
    const [state, setState] = useState<StateType>(initialState)
    const cbRef = useRef<CallbackType | null>(null)

    const setStateCallback = useCallback((state: StateType, cb?: CallbackType) => {
        cbRef.current = cb ?? null
        setState(state)
    }, [])

    useEffect(() => {
        if (cbRef.current) {
            cbRef.current(state)
            cbRef.current = null
        }
    }, [state])

    return [state, setStateCallback]
}

export function useDidMount(fn: () => void) {
    useEffect(fn, [])
}

export function useWillUnmount(fn: () => void) {
    useEffect(() => fn, [])
}

export function readSearchParams(search?: string) {
    if (isBrowser()) {
        return new URLSearchParams(search || window.location.search)
    }
    return new Map<string, string | null>()
}

export function useSearchParams() {
    const { search } = useLocation()
    return readSearchParams(search)
}

export function usePrevious<ValueType>(value: ValueType) {
    const ref = useRef<ValueType>()
    useEffect(() => {
        ref.current = value
    }, [value])
    return ref.current
}

export function useElementSize<T extends HTMLElement>(): [
    (node: T | null) => void,
    { width: number; height: number },
] {
    // unmodified SOURCE https://usehooks-ts.com/react-hook/use-element-size
    const [ref, setRef] = useState<T | null>(null)
    const [size, setSize] = useState({
        width: 0,
        height: 0,
    })

    const handleSize = useCallback(() => {
        setSize({
            width: ref?.offsetWidth || 0,
            height: ref?.offsetHeight || 0,
        })
    }, [ref?.offsetHeight, ref?.offsetWidth])

    useEvent("resize", handleSize)

    useEffect(() => {
        handleSize()
    }, [ref?.offsetHeight, ref?.offsetWidth])

    return [setRef, size]
}
